import { useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import * as pdfjs from 'pdfjs-dist/build/pdf.mjs';

// Serve the worker from a CDN pinned to the exact installed version so it always
// resolves to a real JavaScript file (avoids "text/html is not a valid MIME
// type" errors that happen when the bundled worker URL is served as index.html).
pdfjs.GlobalWorkerOptions.workerSrc =
  'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs';

/**
 * PDF page picker — renders every page of the given File as a thumbnail and
 * lets the user click to toggle include/exclude. Emits an array of KEPT page
 * numbers via `onChange`.
 *
 * Note: props.mode controls the visual: 'keep' (default) marks selected as
 * KEPT; 'delete' marks selected as REMOVED. Both emit the KEPT set for
 * simplicity in the parent state.
 */
export default function PdfPagePicker({ file, mode = 'keep', onChange }) {
  const [pages, setPages] = useState([]); // [{num, dataUrl}]
  const [selected, setSelected] = useState(new Set()); // 1-based page numbers
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const controller = useRef(null);

  // Render thumbnails whenever a new file arrives
  useEffect(() => {
    if (!file) {
      setPages([]);
      setSelected(new Set());
      return;
    }
    setLoading(true);
    setError('');
    controller.current = { cancelled: false };
    const local = controller.current;

    (async () => {
      try {
        const buf = await file.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: buf }).promise;
        const total = pdf.numPages;
        const initSel = new Set();
        for (let i = 1; i <= total; i += 1) initSel.add(i);
        setSelected(initSel);
        const thumbs = [];
        for (let i = 1; i <= total; i += 1) {
          if (local.cancelled) return;
          const p = await pdf.getPage(i);
          const viewport = p.getViewport({ scale: 0.35 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          await p.render({ canvasContext: ctx, viewport, canvas }).promise;
          thumbs.push({ num: i, dataUrl: canvas.toDataURL('image/png') });
          setPages([...thumbs]);
        }
      } catch (e) {
        if (!local.cancelled) setError(e?.message || 'Could not read the PDF');
      } finally {
        if (!local.cancelled) setLoading(false);
      }
    })();

    return () => { if (controller.current) controller.current.cancelled = true; };
  }, [file]);

  // Emit KEPT pages whenever selection changes
  useEffect(() => {
    if (!onChange) return;
    onChange(Array.from(selected).sort((a, b) => a - b));
  }, [selected, onChange]);

  const toggle = (n) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n); else next.add(n);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(pages.map((p) => p.num)));
  const clearAll = () => setSelected(new Set());

  if (!file) return null;

  return (
    <div data-testid="pdf-page-picker" className="mt-6 border-2 border-black bg-white p-5 brutal-shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono-plex text-xs uppercase tracking-widest text-zinc-500">
            {mode === 'delete' ? 'Pick pages to KEEP' : 'Pick pages to include'}
          </p>
          <p className="font-display font-semibold text-lg mt-1">
            {pages.length ? `${pages.length} page${pages.length === 1 ? '' : 's'} in this PDF` : 'Reading PDF…'}
            {pages.length > 0 && (
              <span className="ml-2 text-sm font-normal text-zinc-600">
                — {selected.size} selected
              </span>
            )}
          </p>
        </div>
        {pages.length > 0 && (
          <div className="flex gap-2">
            <button data-testid="picker-select-all" onClick={selectAll} className="brutal-btn secondary text-xs">Select all</button>
            <button data-testid="picker-clear-all" onClick={clearAll} className="brutal-btn secondary text-xs">Clear all</button>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-rose-800 mt-3">{error}</p>}
      {loading && !pages.length && (
        <div className="mt-4 h-40 bg-zinc-100 border-2 border-dashed border-zinc-300 grid place-items-center text-sm text-zinc-500 font-mono-plex">
          Rendering thumbnails…
        </div>
      )}

      {pages.length > 0 && (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {pages.map((p) => {
            const isSelected = selected.has(p.num);
            return (
              <button
                type="button"
                key={p.num}
                data-testid={`page-thumb-${p.num}`}
                data-selected={isSelected ? 'true' : 'false'}
                onClick={() => toggle(p.num)}
                className={`relative block border-2 p-1 group ${
                  isSelected ? 'border-black bg-white' : 'border-zinc-300 bg-zinc-50 opacity-60'
                } hover:-translate-y-0.5`}
                style={{ transition: 'transform 0.15s ease, opacity 0.15s ease' }}
              >
                <img src={p.dataUrl} alt={`Page ${p.num}`} className="w-full h-auto block" />
                <span className={`absolute top-1 right-1 grid place-items-center rounded-none w-6 h-6 border-2 border-black text-xs font-bold ${
                  isSelected ? 'bg-emerald-400' : 'bg-rose-400'
                }`}>
                  {isSelected ? <CheckCircle weight="fill" size={14} /> : <XCircle weight="fill" size={14} />}
                </span>
                <span className="absolute bottom-1 left-1 bg-black text-white text-xs font-mono-plex px-1.5 py-0.5">
                  {p.num}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
